<template>
    <section class="dp-layout blackboard-config-page">
        <section class="dp-left">
            <DepartmentPane directory-scope="school" @tree-node-click="treeNodeClick" :type="1"></DepartmentPane>
        </section>

        <section class="dp-right">
            <template v-if="schoolId > 0">
                <div class="header-bar">
                    <div class="school-info">
                        <span class="label">当前学校：</span>
                        <span class="value">{{ schoolName || '未命名学校' }}</span>
                    </div>
                </div>

                <el-alert
                    title="各校区选择一个值日检查一级类别，并配置校区同年级名次对应的德育分。导入表里的分数只用于排名，写入值周的是这里配置的分。"
                    type="info"
                    :closable="false"
                    show-icon
                    class="tip-alert"
                />

                <el-table :data="configList" v-loading="loading" border style="width: 100%;" class="compact-table">
                    <el-table-column prop="CampusName" label="校区" min-width="140" show-overflow-tooltip />
                    <el-table-column label="一级类别" min-width="220" show-overflow-tooltip>
                        <template slot-scope="scope">
                            <span v-if="scope.row.CategoryInvalid" class="warn-text">配置已失效，请重新选择</span>
                            <span v-else-if="scope.row.CategoryName">{{ scope.row.CategoryName }}</span>
                            <span v-else class="muted">未配置</span>
                        </template>
                    </el-table-column>
                    <el-table-column label="名次加分" min-width="240" show-overflow-tooltip>
                        <template slot-scope="scope">
                            <span v-if="formatRankRules(scope.row.RankRules)">{{ formatRankRules(scope.row.RankRules) }}</span>
                            <span v-else class="muted">未配置</span>
                        </template>
                    </el-table-column>
                    <el-table-column label="操作" width="120" align="center" fixed="right">
                        <template slot-scope="scope">
                            <el-button
                                v-if="hasActionPermission('openEditDialog')"
                                type="primary"
                                size="mini"
                                @click="openEditDialog(scope.row)"
                            >配置</el-button>
                        </template>
                    </el-table-column>
                </el-table>
            </template>

            <div v-else class="no-department-tip">
                <div class="no-department-tip__inner">
                    <i class="el-icon-school"></i>
                    <p>请在左侧选择所属学校，配置各校区黑板报类别</p>
                </div>
            </div>
        </section>

        <el-dialog
            title="黑板报配置"
            :visible.sync="dialogVisible"
            width="640px"
            append-to-body
            custom-class="bb-config-dialog"
            :close-on-click-modal="false"
            @closed="onDialogClosed"
        >
            <el-form ref="editForm" :model="editForm" :rules="editRules" label-width="96px" size="small" class="bb-form">
                <el-form-item label="校区">
                    <span class="campus-name">{{ editForm.CampusName }}</span>
                </el-form-item>
                <el-form-item label="一级类别" prop="CategoryId">
                    <el-select
                        v-model="editForm.CategoryId"
                        placeholder="请选择一级类别"
                        filterable
                        clearable
                        style="width: 100%;"
                        :loading="optionsLoading"
                    >
                        <el-option
                            v-for="opt in categoryOptions"
                            :key="opt.Id"
                            :label="formatOptionLabel(opt)"
                            :value="opt.Id"
                        />
                    </el-select>
                </el-form-item>
            </el-form>

            <div class="rule-panel">
                <div class="rule-panel__head">
                    <div class="rule-panel__copy">
                        <div class="rule-panel__title">名次加分</div>
                        <p class="rule-panel__desc">按校区同年级名次，范围不能重叠。默认覆盖到第 50 名，最后一条结束名次填 999，表示该名次及以后。没覆盖到的名次只记排名。</p>
                    </div>
                    <el-button size="small" icon="el-icon-plus" @click="addRule">添加范围</el-button>
                </div>
                <template v-if="editForm.RankRules.length">
                    <div class="rule-grid rule-grid--head">
                        <span>起始名次</span>
                        <span>结束名次</span>
                        <span>德育分</span>
                        <span></span>
                    </div>
                    <div
                        v-for="(rule, index) in editForm.RankRules"
                        :key="rule._key"
                        class="rule-grid rule-line"
                        :class="{ 'is-overlap': isOverlapRule(index) }"
                    >
                        <el-input-number
                            v-model="rule.RankFrom"
                            :min="1"
                            :max="999"
                            :controls="false"
                            size="small"
                        />
                        <el-input-number
                            v-model="rule.RankTo"
                            :min="1"
                            :max="999"
                            :controls="false"
                            size="small"
                        />
                        <el-input-number
                            v-model="rule.Points"
                            :precision="1"
                            :step="1"
                            :controls="false"
                            size="small"
                        />
                        <el-button type="text" class="rule-del" @click="removeRule(index)">删除</el-button>
                    </div>
                </template>
                <p v-else class="rule-empty">还没有名次范围</p>
                <el-alert
                    v-if="rankRuleHint()"
                    :title="rankRuleHint()"
                    type="error"
                    :closable="false"
                    show-icon
                    class="rule-hint"
                />
            </div>
            <div slot="footer">
                <el-button size="small" @click="dialogVisible = false">取消</el-button>
                <el-button type="primary" size="small" :loading="saving" @click="handleSave">保存</el-button>
            </div>
        </el-dialog>
    </section>
</template>

<script>
import DepartmentPane from '../../components/DepartmentPane';
import { getButtonList } from '../../promissionRouter';
import {
    getBlackboardConfigList,
    getBlackboardCategoryOptions,
    saveBlackboardConfig
} from '../../api/modules/blackboard';
import { normalizeDeptId, deptIdsEqual, resolveActiveSchoolId } from '../../utils/deptCascaderProps';

export default {
    name: 'BlackboardConfig',
    components: { DepartmentPane },
    data() {
        return {
            schoolId: 0,
            schoolName: '',
            boundSchoolId: 0,
            buttonList: [],
            loading: false,
            saving: false,
            optionsLoading: false,
            configList: [],
            dialogVisible: false,
            categoryOptions: [],
            editForm: {
                CampusId: 0,
                CampusName: '',
                CategoryId: null,
                RankRules: []
            },
            editRules: {
                CategoryId: [{ required: true, message: '请选择一级类别', trigger: 'change' }]
            }
        };
    },
    created() {
        this.buttonList = getButtonList(this.$route.path) || [];
        const sid = this.resolveSchoolId();
        if (sid) {
            this.applySchoolChange(sid, this.$store.state.activeSchoolName);
        }
    },
    watch: {
        '$store.state.activeSchoolId'(newSchoolId) {
            this.applySchoolChange(newSchoolId, this.$store.state.activeSchoolName);
        }
    },
    methods: {
        hasActionPermission(func) {
            if (!this.buttonList || !this.buttonList.length) return true;
            return this.buttonList.some(button => button.Func === func);
        },

        /** 解析当前学校 Id（字符串，避免雪花 Id 被 Number 截断）。最后修改：2026-10-18 */
        resolveSchoolId() {
            const fromStore = resolveActiveSchoolId(this);
            if (fromStore) return fromStore;
            const local = normalizeDeptId(this.schoolId);
            if (local) return local;
            return '';
        },

        applySchoolChange(schoolId, schoolName) {
            const id = normalizeDeptId(schoolId);
            if (!id) return;
            if (deptIdsEqual(id, this.schoolId) && deptIdsEqual(this.boundSchoolId, id)) return;
            this.schoolId = id;
            this.boundSchoolId = id;
            if (schoolName) this.schoolName = schoolName;
            this.loadConfigList();
        },

        treeNodeClick(data) {
            this.applySchoolChange(data && data.value, data && data.label);
        },

        formatRankRules(rules) {
            if (!rules || !rules.length) return '';
            return rules.map(rule => {
                const from = Number(rule.RankFrom);
                const to = Number(rule.RankTo);
                const range = to >= 999
                    ? `第${from}名及以后`
                    : (from === to ? `第${from}名` : `第${from}-${to}名`);
                return `${range} ${rule.Points}分`;
            }).join('；');
        },

        stampRule(rule, index) {
            return {
                RankFrom: rule.RankFrom,
                RankTo: rule.RankTo,
                Points: rule.Points,
                _key: `${Date.now()}-${index}-${Math.random().toString(36).slice(2, 6)}`
            };
        },

        /** 未配置时的默认范围：覆盖到第 50 名，再加一条及以后。最后修改：2026-09-24 */
        defaultRankRules() {
            return [
                { RankFrom: 1, RankTo: 1, Points: 0 },
                { RankFrom: 2, RankTo: 3, Points: 0 },
                { RankFrom: 4, RankTo: 50, Points: 0 },
                { RankFrom: 51, RankTo: 999, Points: 0 }
            ];
        },

        addRule() {
            const rules = this.editForm.RankRules || [];
            const maxTo = rules.reduce((max, rule) => Math.max(max, Number(rule.RankTo) || 0), 0);
            const next = maxTo > 0 ? maxTo + 1 : 1;
            if (next > 999) {
                this.$message.warning('名次已经到 999，不能再添加');
                return;
            }
            rules.push(this.stampRule({ RankFrom: next, RankTo: next, Points: 0 }, rules.length));
        },

        removeRule(index) {
            this.editForm.RankRules.splice(index, 1);
        },

        validateRankRules(rules) {
            if (!rules || !rules.length) return '请至少配置一条名次加分';
            const sorted = rules.slice().sort((a, b) => Number(a.RankFrom) - Number(b.RankFrom));
            let prevTo = 0;
            for (let i = 0; i < sorted.length; i++) {
                const from = Number(sorted[i].RankFrom);
                const to = Number(sorted[i].RankTo);
                if (!from || !to || from < 1 || to < from || to > 999) {
                    return '名次范围须为 1 到 999，且结束名次不能小于开始名次';
                }
                if (prevTo > 0 && from <= prevTo) return '名次范围不能重叠';
                if (sorted[i].Points === null || sorted[i].Points === undefined || sorted[i].Points === '') {
                    return '请填写德育分';
                }
                prevTo = to;
            }
            return '';
        },

        rankRuleHint() {
            if (!this.editForm.RankRules || !this.editForm.RankRules.length) return '';
            const msg = this.validateRankRules(this.editForm.RankRules);
            if (!msg || msg === '请填写德育分') return '';
            return msg;
        },

        /** 当前行是否与其他范围重叠，用于标红。最后修改：2026-09-24 */
        isOverlapRule(index) {
            const rules = this.editForm.RankRules || [];
            const current = rules[index];
            if (!current) return false;
            const from = Number(current.RankFrom);
            const to = Number(current.RankTo);
            if (!from || !to || to < from) return false;
            return rules.some((rule, i) => {
                if (i === index) return false;
                const otherFrom = Number(rule.RankFrom);
                const otherTo = Number(rule.RankTo);
                if (!otherFrom || !otherTo || otherTo < otherFrom) return false;
                return from <= otherTo && otherFrom <= to;
            });
        },

        formatOptionLabel(opt) {
            if (!opt) return '';
            if (opt.IsSchoolWide || !opt.CampusName) return opt.Name;
            return `${opt.Name}（${opt.CampusName}）`;
        },

        async loadConfigList() {
            if (!this.schoolId) return;
            this.loading = true;
            try {
                const res = await getBlackboardConfigList({ schoolId: this.schoolId });
                if (res.data && res.data.success) {
                    this.configList = res.data.response || [];
                } else {
                    this.configList = [];
                    this.$message.error((res.data && res.data.msg) || '加载配置失败');
                }
            } catch (e) {
                this.configList = [];
                this.$message.error('加载配置失败');
            } finally {
                this.loading = false;
            }
        },

        async openEditDialog(row) {
            const source = (row.RankRules && row.RankRules.length)
                ? row.RankRules
                : this.defaultRankRules();
            this.editForm = {
                CampusId: row.CampusId,
                CampusName: row.CampusName,
                CategoryId: row.CategoryId > 0 ? row.CategoryId : null,
                RankRules: source.map((rule, index) => this.stampRule(rule, index))
            };
            this.dialogVisible = true;
            this.optionsLoading = true;
            try {
                const res = await getBlackboardCategoryOptions({
                    schoolId: this.schoolId,
                    campusId: row.CampusId || 0
                });
                if (res.data && res.data.success) {
                    this.categoryOptions = res.data.response || [];
                } else {
                    this.categoryOptions = [];
                    this.$message.error((res.data && res.data.msg) || '加载类别失败');
                }
            } catch (e) {
                this.categoryOptions = [];
                this.$message.error('加载类别失败');
            } finally {
                this.optionsLoading = false;
            }
        },

        onDialogClosed() {
            this.categoryOptions = [];
            this.$refs.editForm && this.$refs.editForm.clearValidate();
        },

        handleSave() {
            this.$refs.editForm.validate(async valid => {
                if (!valid) return;
                const ruleMsg = this.validateRankRules(this.editForm.RankRules);
                if (ruleMsg) {
                    this.$message.warning(ruleMsg);
                    return;
                }
                this.saving = true;
                try {
                    const res = await saveBlackboardConfig({
                        schoolId: this.schoolId,
                        campusId: this.editForm.CampusId,
                        categoryId: this.editForm.CategoryId,
                        rankRules: this.editForm.RankRules.map(rule => ({
                            RankFrom: rule.RankFrom,
                            RankTo: rule.RankTo,
                            Points: rule.Points
                        }))
                    });
                    if (res.data && res.data.success) {
                        this.$message.success(res.data.msg || '保存成功');
                        this.dialogVisible = false;
                        this.loadConfigList();
                    } else {
                        this.$message.error((res.data && res.data.msg) || '保存失败');
                    }
                } catch (e) {
                    this.$message.error('保存失败');
                } finally {
                    this.saving = false;
                }
            });
        }
    }
};
</script>

<style scoped>
.header-bar {
    margin-bottom: 12px;
}
.school-info .label {
    color: #909399;
}
.school-info .value {
    font-weight: 600;
}
.tip-alert {
    margin-bottom: 12px;
}
.warn-text {
    color: #E6A23C;
}
.muted {
    color: #909399;
}
.bb-form /deep/ .el-form-item__label {
    white-space: nowrap;
}
.campus-name {
    color: #0f172a;
    font-weight: 600;
    line-height: 32px;
}
.rule-panel {
    margin-top: 4px;
    padding-top: 16px;
    border-top: 1px solid #e8edf3;
}
.rule-panel__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 14px;
}
.rule-panel__copy {
    min-width: 0;
}
.rule-panel__title {
    color: #0f172a;
    font-size: 14px;
    font-weight: 600;
    line-height: 32px;
}
.rule-panel__desc {
    margin: 0;
    color: #475569;
    font-size: 12px;
    line-height: 1.6;
}
.rule-panel__head .el-button {
    flex: none;
}
.rule-grid {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 56px;
    column-gap: 16px;
    align-items: center;
}
.rule-grid--head {
    margin-bottom: 6px;
    color: #64748b;
    font-size: 12px;
    line-height: 1.4;
}
.rule-line {
    padding: 8px 0;
    border-top: 1px solid #e8edf3;
}
.rule-line /deep/ .el-input-number {
    width: 100%;
}
.rule-line /deep/ .el-input__inner {
    padding-left: 12px;
    padding-right: 12px;
    text-align: left;
}
.rule-del {
    padding: 0;
    color: #64748b;
}
.rule-del:hover {
    color: #dc2626;
}
.rule-empty {
    margin: 0;
    padding: 12px 0 4px;
    color: #475569;
    font-size: 13px;
    border-top: 1px solid #e8edf3;
}
.rule-line.is-overlap /deep/ .el-input__inner {
    border-color: #F56C6C;
    color: #F56C6C;
}
.rule-hint {
    margin-top: 10px;
    background: #fef0f0;
    color: #F56C6C;
}
.rule-hint /deep/ .el-alert__title,
.rule-hint /deep/ .el-alert__icon {
    color: #F56C6C;
}
.no-department-tip {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    min-height: 280px;
}
.no-department-tip__inner {
    text-align: center;
    color: #909399;
}
.no-department-tip__inner i {
    font-size: 48px;
    margin-bottom: 12px;
}
</style>
